import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps } from "@/lib/chart";
import { axisINR, formatCount, formatPercent } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ProjectMultiSelect } from "@/components/filters/ProjectMultiSelect";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Gift, Wallet, Home, Coins, CheckCircle2 } from "lucide-react";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export default function ReferralTracker() {
  const { money } = useCurrency();
  const [gran, setGran] = useState("Year");
  const [quarter, setQuarter] = useState("Q1");
  const [month, setMonth] = useState("Apr-26");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [meta, setMeta] = useState({ projects: [], regions: [], categories: [] });
  const [monthsList, setMonthsList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const period = gran === "Year" ? "FY" : gran === "Quarter" ? quarter : month;

  useEffect(() => {
    api.lists().then((l) => {
      const projects = (l.projects || [])
        .map((p) => ({ name: p.name, region: p.region, category: p.category, team: p.team }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setMeta({
        projects,
        regions: Array.from(new Set(projects.map((p) => p.region).filter(Boolean))),
        categories: Array.from(new Set(projects.map((p) => p.category).filter(Boolean))),
      });
      const ms = l.months || [];
      setMonthsList(ms);
      if (ms.length) setMonth((m) => (ms.includes(m) ? m : ms[0]));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.referralTracker(period, selectedProjects.length ? selectedProjects : null)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Could not load referral tracker"); });
  }, [period, JSON.stringify(selectedProjects)]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => data?.rows || [], [data]);
  const barData = useMemo(() => rows.slice(0, 12).map((r) => ({ name: r.project, spend: r.spend })).reverse(), [rows]);
  const trend = data?.trend || [];

  const header = (
    <PageHeader title="Referral Tracker"
      description="CX referral payouts by project and the bookings they helped drive.">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)}
          className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5">
          <ToggleGroupItem value="Year" className="h-8 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="referral-gran-year">Year</ToggleGroupItem>
          <ToggleGroupItem value="Quarter" className="h-8 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="referral-gran-quarter">Quarter</ToggleGroupItem>
          <ToggleGroupItem value="Month" className="h-8 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="referral-gran-month">Month</ToggleGroupItem>
        </ToggleGroup>
        {gran === "Quarter" && (
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="h-9 w-[110px]" data-testid="referral-quarter-select"><SelectValue /></SelectTrigger>
            <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {gran === "Month" && (
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="referral-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthsList.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <ProjectMultiSelect projects={meta.projects} regions={meta.regions} categories={meta.categories}
          selected={selectedProjects} onChange={setSelectedProjects} />
      </div>
    </PageHeader>
  );

  if (loading && !data) return <div className="space-y-5" data-testid="referral-tracker-page">{header}<Loading rows={6} /></div>;
  if (!data || rows.length === 0) return (
    <div className="space-y-5" data-testid="referral-tracker-page">
      {header}
      <EmptyState title="No referral payouts" message="No CX referral payouts were recorded for this period." />
    </div>
  );

  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="referral-tracker-page">
      {header}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <KpiTile testId="kpi-referral-spend" title="Referral Payouts" accent="hsl(var(--chart-cat-6))" icon={Gift} value={<Money value={t.spend} />} sub={`${formatCount(t.count)} payouts · ${formatCount(t.projects)} projects`} />
        <KpiTile testId="kpi-referral-paid" title="Paid" accent="hsl(var(--success))" icon={CheckCircle2} value={formatPercent(t.paid_ratio)} sub={<><Money value={t.paid} /> cleared</>} />
        <KpiTile testId="kpi-referral-outstanding" title="Outstanding" accent="hsl(var(--warning))" icon={Wallet} value={<Money value={t.outstanding} />} sub="Yet to be paid" />
        <KpiTile testId="kpi-referral-bookings" title="CX Bookings" accent="hsl(var(--chart-cat-3))" icon={Home} value={formatCount(t.cx_bookings)} sub="Booking impact (CX)" />
        <KpiTile testId="kpi-referral-cpb" title="Cost / Booking" accent="hsl(var(--primary))" icon={Coins} value={t.cost_per_booking != null ? <Money value={t.cost_per_booking} /> : "—"} sub="Referral spend ÷ CX bookings" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Referral Payouts by Project" description="Where referral spend is concentrated this period." testId="card-referral-projects">
          <div className="px-2 py-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 20, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                <Bar dataKey="spend" name="Referral payout" radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false}>
                  {barData.map((_, i) => <Cell key={i} fill="hsl(var(--chart-cat-6))" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Referral Spend Trend" description="Monthly referral payout run-rate." testId="card-referral-trend">
          <div className="px-2 py-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                <XAxis dataKey="month" {...axisProps} />
                <YAxis tickFormatter={axisINR} {...axisProps} />
                <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                <Bar dataKey="spend" name="Referral payout" fill="hsl(var(--chart-cat-6))" radius={[4, 4, 0, 0]} barSize={30} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Referral by Project" description="Payouts, payment status and booking impact per project." testId="card-referral-table">
        <div className="table-scroll">
          <table className="w-full border-collapse" data-testid="referral-table">
            <thead><tr>
              {["Project", "Payouts", "Count", "Paid %", "Outstanding", "CX Bookings", "Cost / Booking"].map((h, i) => (
                <th key={h} className={cn("th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", i === 0 ? "th-sticky-left text-left" : "text-right")}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.project} className={cn("border-b last:border-0", i % 2 && "row-zebra")} data-testid={`referral-row-${r.project.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                  <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">{r.project}</td>
                  <td className="px-3 py-2.5 num text-right text-sm font-medium"><Money value={r.spend} /></td>
                  <td className="px-3 py-2.5 num text-right text-sm text-muted-foreground">{formatCount(r.count)}</td>
                  <td className="px-3 py-2.5 num text-right text-sm">
                    <span className={r.paid_ratio >= 0.5 ? "text-[hsl(var(--success))]" : r.paid_ratio > 0 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--danger))]"}>{formatPercent(r.paid_ratio, 0)}</span>
                  </td>
                  <td className="px-3 py-2.5 num text-right text-sm"><Money value={r.outstanding} /></td>
                  <td className="px-3 py-2.5 num text-right text-sm">{formatCount(r.cx_bookings)}</td>
                  <td className="px-3 py-2.5 num text-right text-sm font-medium text-[hsl(var(--primary))]">{r.cost_per_booking != null ? <Money value={r.cost_per_booking} /> : "—"}</td>
                </tr>
              ))}
              <tr className="border-t-2 bg-[hsl(var(--surface-2))] font-semibold" data-testid="referral-row-total">
                <td className="td-sticky-left px-3 py-2.5 text-sm">All referral projects</td>
                <td className="px-3 py-2.5 num text-right text-sm"><Money value={t.spend} /></td>
                <td className="px-3 py-2.5 num text-right text-sm">{formatCount(t.count)}</td>
                <td className="px-3 py-2.5 num text-right text-sm">{formatPercent(t.paid_ratio, 0)}</td>
                <td className="px-3 py-2.5 num text-right text-sm"><Money value={t.outstanding} /></td>
                <td className="px-3 py-2.5 num text-right text-sm">{formatCount(t.cx_bookings)}</td>
                <td className="px-3 py-2.5 num text-right text-sm text-[hsl(var(--primary))]">{t.cost_per_booking != null ? <Money value={t.cost_per_booking} /> : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="px-4 py-2 text-[10px] text-muted-foreground">Booking impact uses CX-vertical bookings achieved — referrals are the primary CX booking driver.</p>
      </SectionCard>
    </div>
  );
}
